import React, { useEffect, useState } from 'react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { Box, Button, Paper, Grid, IconButton, Card, Tabs, Tab, Divider, Chip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BarChartIcon from '@mui/icons-material/BarChart';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Utils & Hooks
import logger from './utils/logger';
import useIsEmriQuery from './hooks/useIsEmriQuery';

// Components
import LogDisplay from './components/LogDisplay';
import StatusIndicator, { LoadingButton } from './components/StatusIndicator';
import VisualizationPanel from './components/VisualizationPanel';
import { formatCurrency, formatMinutes } from './helpers';

// Supabase theme constants
export const supaTheme = {
    colors: {
        brand: '#10B981', // Daha belirgin bir yeşil tonu
        secondary: '#34D399', // Daha canlı açık yeşil
        accent: '#6366F1', // Daha canlı mor
        background: {
            light: '#F9FAFB', // Daha açık bir arka plan
            default: '#F1F5F9', // Hafif gri arka plan
            dark: '#E2E8F0', // Sınır rengi
        },
        text: {
            primary: '#111827', // Daha koyu - daha iyi kontrast
            secondary: '#4B5563', // Daha koyu ikincil metin
            light: '#9CA3AF', // Daha açık metin
        },
        status: {
            success: '#10B981', // Success rengi
            warning: '#FBBF24', // Warning rengi
            error: '#EF4444', // Error rengi
        },
    },
    shadows: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    },
    radius: {
        sm: '4px',
        md: '6px',
        lg: '8px',
    },
    fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
};

/**
 * Sipariş bileşeni - Dekont numarasına göre iş emirlerini çeker ve görselleştirir
 * @param {Object} props - Komponent özellikleri
 * @param {string} props.dekontID - Dekont ID
 */
function Siparis({ dekontID }) {
    // State tanımları
    const [logs, setLogs] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [showLogs, setShowLogs] = useState(false);

    // İlk yükleme logu
    useEffect(() => {
        logger.info(`Sipariş görüntüleme ekranı yüklendi. Dekont ID: ${dekontID || 'Belirtilmedi'}`);
        logger.info("Veri çekmek için 'Verileri Yeniden Çek' butonuna tıklayınız.");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Log handler fonksiyonu - logger'ın state'e bağlanması
    useEffect(() => {
        // Log değişikliklerini subscribe et
        const unsubscribe = logger.subscribe((logData) => {
            // Tüm loglar geldiğinde (ilk abonelik veya getAll() sonucu)
            if (Array.isArray(logData)) {
                setLogs(logData);
                console.log('Loglar yüklendi:', logData.length);
            }
            // Loglar temizlendiğinde
            else if (logData.type === 'clear') {
                setLogs([]);
                console.log('Loglar temizlendi');
            }
            // Tekil log eklendiğinde (yeni log)
            else {
                // Logger zaten başa ekliyor, biz de burada başa ekleyelim
                setLogs((prev) => [logData, ...prev]);
                console.log('Yeni log eklendi:', logData.message);
            }
        });

        // Komponent unmount olduğunda aboneliği temizle
        return () => unsubscribe();
    }, []);

    // İş emri verilerini çeken hook
    const { isEmriData, isLoading, error, refetch, cancelFetch } = useIsEmriQuery({
        dekontID,
        logger,
    });

    // Veri yeniden çekme işlemi
    const handleRefetch = () => {
        logger.clear(); // Logları temizle
        logger.info('Veri çekme işlemi başlatılıyor...'); // Test log
        refetch();
    };

    // İptal işlemi
    const handleCancel = () => {
        logger.warning('İşlem kullanıcı tarafından iptal edildi.'); // Test log
        cancelFetch();
    };

    // Tüm verileri hesapla
    const calculateStats = () => {
        let totalAmount = 0;
        let totalDuration = 0;
        let totalPlans = 0;
        let completedPlans = 0;
        let waitingPlans = 0;
        let inProgressPlans = 0;
        let latestUpdateDate = null;

        if (!isEmriData || isEmriData.length === 0) {
            return {
                siparisCount: 0,
                isEmriCount: 0,
                planCount: 0,
                completedCount: 0,
                waitingCount: 0,
                inProgressCount: 0,
                completionRate: 0,
                totalAmount: 0,
                totalDuration: 0,
                latestUpdate: null,
            };
        }

        isEmriData.forEach((item) => {
            // Sipariş tutarı toplama
            if (item?.stokHareketi?.Tutar) {
                totalAmount += item.stokHareketi.Tutar;
            }

            // İş emri ve üretim planları
            if (item?.isEmri) {
                const plans = item.isEmri.uretimPlanlari || [];

                // Planları say
                totalPlans += plans.length;

                // Plan durumları
                plans.forEach((plan) => {
                    if (plan.Kapali) {
                        completedPlans++;
                    } else if (plan.Beklemede) {
                        waitingPlans++;
                    } else {
                        inProgressPlans++;
                    }

                    // Toplam süre
                    if (plan.SureToplam) {
                        totalDuration += plan.SureToplam;
                    }

                    // En son güncelleme tarihi
                    if (plan.GuncellemeTarihi) {
                        const updateDate = new Date(plan.GuncellemeTarihi);
                        if (!latestUpdateDate || updateDate > latestUpdateDate) {
                            latestUpdateDate = updateDate;
                        }
                    }
                });
            }
        });

        return {
            siparisCount: isEmriData.length || 0,
            isEmriCount: isEmriData.reduce((count, item) => count + (item?.isEmri ? 1 : 0), 0) || 0,
            planCount: totalPlans,
            completedCount: completedPlans,
            waitingCount: waitingPlans,
            inProgressCount: inProgressPlans,
            completionRate: totalPlans > 0 ? Math.round((completedPlans / totalPlans) * 100) : 0,
            totalAmount: totalAmount,
            totalDuration: totalDuration,
            latestUpdate: latestUpdateDate,
        };
    };

    // Özet istatistikleri hesapla
    const stats = calculateStats();

    // Görselleştirme türleri
    const visTypes = [
        { id: 'indentedTree', name: 'Girintili Ağaç' },
        { id: 'circlePacking', name: 'Circle Packing' },
        { id: 'flow', name: 'Akış Diyagramı' },
        { id: 'tree', name: 'Ağaç Diyagramı' },
        { id: 'sunburst', name: 'Sunburst' },
    ];

    return (
        <MDBox
            p={0}
            sx={{
                backgroundColor: supaTheme.colors.background.default,
                borderRadius: supaTheme.radius.md,
                fontFamily: supaTheme.fontFamily,
                minHeight: '100vh',
                width: '100%',
                color: supaTheme.colors.text.primary,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '18px 28px',
                    borderBottom: `1px solid ${"var(--dark-background-supaTheme-colors)"}`,
                    backgroundColor: 'white',
                }}
                style={{
                    "--dark-background-supaTheme-colors": supaTheme.colors.background.dark
                }}>
                <MDTypography
                    variant="h6"
                    sx={{
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        fontFamily: supaTheme.fontFamily,
                    }}
                >
                    Sipariş Bilgileri {dekontID ? `- Dekont #${dekontID}` : ''}
                </MDTypography>

                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5
                    }}>
                    <Button
                        variant="text"
                        size="medium"
                        onClick={() => setShowLogs(!showLogs)}
                        sx={{
                            color: supaTheme.colors.text.secondary,
                            textTransform: 'none',
                            fontSize: '0.9375rem',
                            fontWeight: 500,
                            fontFamily: supaTheme.fontFamily,
                            padding: '6px 16px',
                        }}
                        startIcon={<InfoOutlinedIcon />}
                    >
                        {showLogs ? 'Logları Gizle' : `Loglar (${logs.length})`}
                    </Button>

                    <LoadingButton
                        loading={isLoading}
                        onClick={handleRefetch}
                        text="Verileri Yeniden Çek"
                        color={supaTheme.colors.brand}
                        hoverColor={supaTheme.colors.secondary}
                        fontSize="0.9375rem"
                        padding="8px 20px"
                    />

                    {isLoading && (
                        <Button
                            variant="outlined"
                            size="medium"
                            sx={{
                                color: supaTheme.colors.status.warning,
                                borderColor: supaTheme.colors.status.warning,
                                textTransform: 'none',
                                fontSize: '0.9375rem',
                                fontWeight: 500,
                                fontFamily: supaTheme.fontFamily,
                                padding: '6px 16px',
                                '&:hover': {
                                    borderColor: supaTheme.colors.status.warning,
                                    backgroundColor: 'rgba(251, 191, 36, 0.08)',
                                },
                            }}
                            onClick={handleCancel}
                        >
                            İptal
                        </Button>
                    )}
                </Box>
            </Box>
            {/* Content Area */}
            <Grid container spacing={0}>
                {/* Left-side summary panel (fixed width) */}
                <Grid
                    sx={[{
                        borderRight: `1px solid ${"var(--dark-background-supaTheme-colors)"}`,
                        backgroundColor: 'white',
                        p: 3.5,
                        position: 'sticky',
                        top: 65,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3.5
                    }, showLogs ? {
                        minHeight: 'calc(100vh - 65px - 300px)'
                    } : {
                        minHeight: 'calc(100vh - 65px)'
                    }]}
                    style={{
                        "--dark-background-supaTheme-colors": supaTheme.colors.background.dark
                    }}
                    size={{
                        xs: 12,
                        md: 3
                    }}>
                    {/* Durum Göstergesi */}
                    {isLoading && (
                        <Card
                            elevation={0}
                            sx={{
                                backgroundColor: 'rgba(251, 191, 36, 0.08)',
                                border: `1px solid ${"var(--warning-status-supaTheme-colors)"}`,
                                p: 2.5,
                                borderRadius: supaTheme.radius.md,
                            }}
                            style={{
                                "--warning-status-supaTheme-colors": supaTheme.colors.status.warning
                            }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start"
                                }}>
                                <WarningIcon sx={{ color: supaTheme.colors.status.warning, fontSize: 22 }} />
                                <MDTypography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.9375rem',
                                        lineHeight: 1.6,
                                        fontFamily: supaTheme.fontFamily,
                                    }}
                                >
                                    Veri çekimi devam ediyor. Bu işlem biraz zaman alabilir.
                                </MDTypography>
                            </Box>
                        </Card>
                    )}

                    {error && (
                        <Card
                            elevation={0}
                            sx={{
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                border: `1px solid ${"var(--error-status-supaTheme-colors)"}`,
                                p: 2.5,
                                borderRadius: supaTheme.radius.md,
                            }}
                            style={{
                                "--error-status-supaTheme-colors": supaTheme.colors.status.error
                            }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start"
                                }}>
                                <CloseIcon sx={{ color: supaTheme.colors.status.error, fontSize: 22 }} />
                                <MDTypography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.9375rem',
                                        lineHeight: 1.6,
                                        fontFamily: supaTheme.fontFamily,
                                    }}
                                >
                                    Hata: {error.message || String(error)}
                                </MDTypography>
                            </Box>
                        </Card>
                    )}

                    {!isLoading && !error && isEmriData.length > 0 && (
                        <Card
                            elevation={0}
                            sx={{
                                backgroundColor: 'rgba(16, 185, 129, 0.08)',
                                border: `1px solid ${"var(--success-status-supaTheme-colors)"}`,
                                p: 2.5,
                                borderRadius: supaTheme.radius.md,
                            }}
                            style={{
                                "--success-status-supaTheme-colors": supaTheme.colors.status.success
                            }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start"
                                }}>
                                <TaskAltIcon sx={{ color: supaTheme.colors.status.success, fontSize: 22 }} />
                                <MDTypography
                                    variant="body2"
                                    sx={{
                                        fontSize: '0.9375rem',
                                        lineHeight: 1.6,
                                        fontFamily: supaTheme.fontFamily,
                                    }}
                                >
                                    Veriler başarıyla yüklendi.
                                </MDTypography>
                            </Box>
                        </Card>
                    )}

                    {/* Özet İstatistikler */}
                    {isEmriData.length > 0 && (
                        <Card
                            elevation={0}
                            sx={{
                                border: `1px solid ${"var(--dark-background-supaTheme-colors)"}`,
                                p: 2.5,
                                borderRadius: supaTheme.radius.md,
                            }}
                            style={{
                                "--dark-background-supaTheme-colors": supaTheme.colors.background.dark
                            }}>
                            <MDTypography
                                variant="subtitle2"
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    mb: 2.5,
                                    fontFamily: supaTheme.fontFamily,
                                    color: supaTheme.colors.text.primary,
                                }}
                            >
                                Genel Bakış
                            </MDTypography>

                            <Grid container spacing={2.5}>
                                {/* Sipariş Sayısı */}
                                <Grid size={6}>
                                    <MDBox
                                        sx={{
                                            p: 2,
                                            backgroundColor: supaTheme.colors.background.light,
                                            borderRadius: supaTheme.radius.sm,
                                            border: `1px solid ${"var(--dark-background-supaTheme-colors)"}`,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                        style={{
                                            "--dark-background-supaTheme-colors": supaTheme.colors.background.dark
                                        }}>
                                        <MDTypography
                                            variant="caption"
                                            component="div"
                                            sx={{
                                                fontSize: '0.8125rem',
                                                color: supaTheme.colors.text.secondary,
                                                fontFamily: supaTheme.fontFamily,
                                                mb: 0.5,
                                            }}
                                        >
                                            Sipariş Sayısı
                                        </MDTypography>
                                        <MDTypography
                                            variant="h6"
                                            sx={{
                                                fontSize: '1.5rem',
                                                fontWeight: 600,
                                                fontFamily: supaTheme.fontFamily,
                                                color: supaTheme.colors.text.primary,
                                            }}
                                        >
                                            {stats.siparisCount}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>

                                {/* İş Emri Sayısı */}
                                <Grid size={6}>
                                    <MDBox
                                        sx={{
                                            p: 2,
                                            backgroundColor: supaTheme.colors.background.light,
                                            borderRadius: supaTheme.radius.sm,
                                            border: `1px solid ${"var(--dark-background-supaTheme-colors)"}`,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                        style={{
                                            "--dark-background-supaTheme-colors": supaTheme.colors.background.dark
                                        }}>
                                        <MDTypography
                                            variant="caption"
                                            component="div"
                                            sx={{
                                                fontSize: '0.8125rem',
                                                color: supaTheme.colors.text.secondary,
                                                fontFamily: supaTheme.fontFamily,
                                                mb: 0.5,
                                            }}
                                        >
                                            İş Emri Sayısı
                                        </MDTypography>
                                        <MDTypography
                                            variant="h6"
                                            sx={{
                                                fontSize: '1.5rem',
                                                fontWeight: 600,
                                                fontFamily: supaTheme.fontFamily,
                                                color: supaTheme.colors.text.primary,
                                            }}
                                        >
                                            {stats.isEmriCount}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>

                                {/* Plan Sayısı */}
                                <Grid size={6}>
                                    <MDBox
                                        sx={{
                                            p: 2,
                                            backgroundColor: supaTheme.colors.background.light,
                                            borderRadius: supaTheme.radius.sm,
                                            border: `1px solid ${"var(--dark-background-supaTheme-colors)"}`,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                        style={{
                                            "--dark-background-supaTheme-colors": supaTheme.colors.background.dark
                                        }}>
                                        <MDTypography
                                            variant="caption"
                                            component="div"
                                            sx={{
                                                fontSize: '0.8125rem',
                                                color: supaTheme.colors.text.secondary,
                                                fontFamily: supaTheme.fontFamily,
                                                mb: 0.5,
                                            }}
                                        >
                                            Toplam Üretim Planı
                                        </MDTypography>
                                        <MDTypography
                                            variant="h6"
                                            sx={{
                                                fontSize: '1.5rem',
                                                fontWeight: 600,
                                                fontFamily: supaTheme.fontFamily,
                                                color: supaTheme.colors.text.primary,
                                            }}
                                        >
                                            {stats.planCount}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>

                                {/* Tamamlanan Plan Sayısı */}
                                <Grid size={6}>
                                    <MDBox
                                        sx={{
                                            p: 2,
                                            backgroundColor: supaTheme.colors.background.light,
                                            borderRadius: supaTheme.radius.sm,
                                            border: `1px solid ${"var(--dark-background-supaTheme-colors)"}`,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                        style={{
                                            "--dark-background-supaTheme-colors": supaTheme.colors.background.dark
                                        }}>
                                        <MDTypography
                                            variant="caption"
                                            component="div"
                                            sx={{
                                                fontSize: '0.8125rem',
                                                color: supaTheme.colors.text.secondary,
                                                fontFamily: supaTheme.fontFamily,
                                                mb: 0.5,
                                            }}
                                        >
                                            Tamamlanan Planlar
                                        </MDTypography>
                                        <MDTypography
                                            variant="h6"
                                            sx={{
                                                fontSize: '1.5rem',
                                                fontWeight: 600,
                                                fontFamily: supaTheme.fontFamily,
                                                color: supaTheme.colors.status.success,
                                            }}
                                        >
                                            {stats.completedCount}
                                        </MDTypography>
                                    </MDBox>
                                </Grid>
                            </Grid>

                            {/* Ek detay bilgileri */}
                            <Box
                                sx={{
                                    mt: 3,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2
                                }}>
                                {/* Tamamlanma Oranı */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5
                                    }}>
                                    <BarChartIcon sx={{ color: supaTheme.colors.brand, fontSize: 20 }} />
                                    <Box sx={{
                                        flex: 1
                                    }}>
                                        <MDTypography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                color: supaTheme.colors.text.primary,
                                                fontFamily: supaTheme.fontFamily,
                                                mb: 0.5,
                                            }}
                                        >
                                            Tamamlanma Oranı
                                        </MDTypography>

                                        {/* İlerleme çubuğu */}
                                        <Box
                                            sx={{
                                                width: '100%',
                                                position: 'relative',
                                                height: 6,
                                                backgroundColor: '#e2e8f0',
                                                borderRadius: 8,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    height: '100%',
                                                    width: `${"var(--stats-completionRate)"}%`,
                                                    backgroundColor: supaTheme.colors.brand,
                                                    borderRadius: 8,
                                                }}
                                                style={{
                                                    "--stats-completionRate": stats.completionRate
                                                }} />
                                        </Box>

                                        <MDTypography
                                            variant="body2"
                                            sx={[{
                                                fontSize: '0.875rem',
                                                mt: 0.5,
                                                fontFamily: supaTheme.fontFamily,
                                                fontWeight: 500,
                                                textAlign: 'right'
                                            }, stats.completionRate > 50 ? {
                                                color: supaTheme.colors.brand
                                            } : {
                                                color: supaTheme.colors.text.secondary
                                            }]}
                                        >
                                            {stats.completionRate}%
                                        </MDTypography>
                                    </Box>
                                </Box>

                                {/* Toplam Tutar */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5
                                    }}>
                                    <AttachMoneyIcon sx={{ color: supaTheme.colors.brand, fontSize: 20 }} />
                                    <Box>
                                        <MDTypography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                color: supaTheme.colors.text.primary,
                                                fontFamily: supaTheme.fontFamily,
                                            }}
                                        >
                                            Toplam Tutar
                                        </MDTypography>
                                        <MDTypography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.9375rem',
                                                fontWeight: 600,
                                                color: supaTheme.colors.text.primary,
                                                fontFamily: supaTheme.fontFamily,
                                            }}
                                        >
                                            {formatCurrency(stats.totalAmount)}
                                        </MDTypography>
                                    </Box>
                                </Box>

                                {/* Toplam Üretim Süresi */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5
                                    }}>
                                    <AccessTimeIcon sx={{ color: supaTheme.colors.brand, fontSize: 20 }} />
                                    <Box>
                                        <MDTypography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                color: supaTheme.colors.text.primary,
                                                fontFamily: supaTheme.fontFamily,
                                            }}
                                        >
                                            Toplam Üretim Süresi
                                        </MDTypography>
                                        <MDTypography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.9375rem',
                                                fontWeight: 600,
                                                color: supaTheme.colors.text.primary,
                                                fontFamily: supaTheme.fontFamily,
                                            }}
                                        >
                                            {formatMinutes(stats.totalDuration)}
                                        </MDTypography>
                                    </Box>
                                </Box>

                                {/* Plan Durumu Detayları */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5
                                    }}>
                                    <BarChartIcon sx={{ color: supaTheme.colors.brand, fontSize: 20 }} />
                                    <Box>
                                        <MDTypography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                color: supaTheme.colors.text.primary,
                                                fontFamily: supaTheme.fontFamily,
                                                mb: 0.75,
                                            }}
                                        >
                                            Plan Durumları
                                        </MDTypography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1
                                            }}>
                                            {stats.completedCount > 0 && (
                                                <Chip
                                                    label={`Tamamlanan: ${stats.completedCount}`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                                        color: supaTheme.colors.status.success,
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem',
                                                    }}
                                                />
                                            )}
                                            {stats.inProgressCount > 0 && (
                                                <Chip
                                                    label={`Devam Eden: ${stats.inProgressCount}`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'rgba(251, 191, 36, 0.1)',
                                                        color: supaTheme.colors.status.warning,
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem',
                                                    }}
                                                />
                                            )}
                                            {stats.waitingCount > 0 && (
                                                <Chip
                                                    label={`Bekleyen: ${stats.waitingCount}`}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                                        color: supaTheme.colors.accent,
                                                        fontWeight: 500,
                                                        fontSize: '0.75rem',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Son Güncelleme */}
                                {stats.latestUpdate && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5
                                        }}>
                                        <CalendarTodayIcon sx={{ color: supaTheme.colors.brand, fontSize: 20 }} />
                                        <Box>
                                            <MDTypography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500,
                                                    color: supaTheme.colors.text.primary,
                                                    fontFamily: supaTheme.fontFamily,
                                                }}
                                            >
                                                Son Güncelleme
                                            </MDTypography>
                                            <MDTypography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '0.875rem',
                                                    color: supaTheme.colors.text.secondary,
                                                    fontFamily: supaTheme.fontFamily,
                                                }}
                                            >
                                                {stats.latestUpdate.toLocaleDateString('tr-TR', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </MDTypography>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Card>
                    )}

                    {/* Görselleştirme Seçimi */}
                    {isEmriData.length > 0 && (
                        <Box>
                            <MDTypography
                                variant="subtitle2"
                                sx={{
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    mb: 2,
                                    fontFamily: supaTheme.fontFamily,
                                    color: supaTheme.colors.text.primary,
                                }}
                            >
                                Görselleştirme Türü
                            </MDTypography>

                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1.5
                                }}>
                                {visTypes.map((type, index) => (
                                    <Button
                                        key={type.id}
                                        variant={activeTab === index ? 'contained' : 'outlined'}
                                        sx={[{
                                            borderRadius: supaTheme.radius.md,
                                            fontFamily: supaTheme.fontFamily,
                                            justifyContent: 'flex-start',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            fontSize: '0.9375rem',
                                            padding: '10px 16px'
                                        }, activeTab === index ? {
                                            backgroundColor: supaTheme.colors.brand
                                        } : {
                                            backgroundColor: 'transparent'
                                        }, activeTab === index ? {
                                            color: 'white'
                                        } : {
                                            color: supaTheme.colors.text.primary
                                        }, activeTab === index ? {
                                            borderColor: supaTheme.colors.brand
                                        } : {
                                            borderColor: supaTheme.colors.background.dark
                                        }, activeTab === index ? {
                                            '&:hover': {
                                                backgroundColor: supaTheme.colors.secondary
                                            }
                                        } : {
                                            '&:hover': {
                                                backgroundColor: 'rgba(16, 185, 129, 0.08)'
                                            }
                                        }]}
                                        onClick={() => setActiveTab(index)}
                                    >
                                        {type.name}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {isEmriData.length === 0 && !isLoading && (
                        <Card
                            elevation={0}
                            sx={{
                                border: `1px solid ${"var(--dark-background-supaTheme-colors)"}`,
                                p: 3.5,
                                borderRadius: supaTheme.radius.md,
                                textAlign: 'center',
                                backgroundColor: supaTheme.colors.background.light,
                            }}
                            style={{
                                "--dark-background-supaTheme-colors": supaTheme.colors.background.dark
                            }}>
                            <HelpOutlineIcon
                                sx={{
                                    fontSize: 56,
                                    color: supaTheme.colors.text.secondary,
                                    mb: 1.5,
                                }}
                            />
                            <MDTypography
                                variant="body2"
                                sx={{
                                    fontSize: '0.9375rem',
                                    fontFamily: supaTheme.fontFamily,
                                    color: supaTheme.colors.text.secondary,
                                    mb: 2,
                                    lineHeight: 1.6,
                                }}
                            >
                                Henüz veri çekilmemiş. Sipariş ve iş emri bilgilerini görmek için "Verileri Yeniden Çek"
                                butonuna tıklayın.
                            </MDTypography>
                        </Card>
                    )}
                </Grid>

                {/* Right-side visualization panel (flexible width) */}
                <Grid
                    sx={[{
                        p: 0,
                        backgroundColor: 'white',
                        display: 'flex',
                        flexDirection: 'column'
                    }, showLogs ? {
                        minHeight: 'calc(100vh - 65px - 300px)'
                    } : {
                        minHeight: 'calc(100vh - 65px)'
                    }]}
                    size={{
                        xs: 12,
                        md: 9
                    }}>
                    {isEmriData.length > 0 ? (
                        <Box sx={{ height: '100%', p: 3 }}>
                            {visTypes.map((type, index) => (
                                <Box
                                    key={type.id}
                                    sx={[{
                                        height: '100%'
                                    }, activeTab === index ? {
                                        display: 'block'
                                    } : {
                                        display: 'none'
                                    }]}
                                >
                                    <VisualizationPanel
                                        activeVisualization={type.id}
                                        isEmriData={isEmriData}
                                        theme={supaTheme}
                                    />
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                backgroundColor: supaTheme.colors.background.light,
                            }}
                        >
                            <MDTypography
                                variant="body1"
                                sx={{
                                    color: supaTheme.colors.text.secondary,
                                    fontFamily: supaTheme.fontFamily,
                                    fontSize: '1rem',
                                }}
                            >
                                {isLoading ? 'Veriler yükleniyor...' : 'Görselleştirme için veri bulunmamaktadır.'}
                            </MDTypography>
                        </Box>
                    )}
                </Grid>
            </Grid>
            {/* Log Display Area (collapsible) */}
            {showLogs && (
                <Box
                    sx={{
                        height: '300px',
                        borderTop: `1px solid ${"var(--dark-background-supaTheme-colors)"}`,
                        padding: '16px 28px',
                        backgroundColor: 'white',
                    }}
                    style={{
                        "--dark-background-supaTheme-colors": supaTheme.colors.background.dark
                    }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2
                        }}>
                        <MDTypography
                            variant="subtitle2"
                            sx={{
                                fontSize: '1rem',
                                fontWeight: 600,
                                fontFamily: supaTheme.fontFamily,
                                color: supaTheme.colors.text.primary,
                            }}
                        >
                            Veri Çekme Logları ({logs.length})
                        </MDTypography>
                        <IconButton size="small" onClick={() => setShowLogs(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ height: 'calc(300px - 70px)', overflow: 'auto' }}>
                        <LogDisplay logs={logs} theme={supaTheme} />
                    </Box>
                </Box>
            )}
        </MDBox>
    );
}

export default Siparis;
